"use client";
import { useCallback, useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"

// --- Icons ---
import { TypeIcon } from "@/tiptap/icons/type-icon"

// --- Lib ---
import {
  FOCUS_NO_SCROLL,
  findConvertibleAncestor,
  findNodePosition,
  getSelectedBlockNodes,
  isNodeInSchema,
  isValidPosition,
  runWithPreservedWindowScroll,
  selectionWithinConvertibleTypes,
} from "@/tiptap/lib/tiptap-utils"

export const TEXT_SHORTCUT_KEY = "mod+alt+0"

/**
 * Returns whether we can toggle/turn the current selection into a paragraph.
 * - When `turnInto === false`, it just checks the direct ability to set paragraph on the selection.
 * - When `turnInto === true`, it additionally requires the selection to be within convertible types.
 */
export function canToggleText(editor, turnInto = true) {
  if (!editor) return false
  if (!editor.schema.nodes.paragraph) return false

  if (!turnInto) {
    return editor.can().setNode("paragraph");
  }

  // Ensure selection is in nodes we're allowed to convert
  if (
    !selectionWithinConvertibleTypes(editor, [
      "paragraph",
      "heading",
      "bulletList",
      "orderedList",
      "taskList",
      "blockquote",
      "callout",
      "codeBlock",
    ])
  )
    return false

  // Either we can set paragraph directly on the selection,
  // or we can clear formatting/nodes to arrive at a paragraph.
  return editor.can().setNode("paragraph") || editor.can().clearNodes();
}

/**
 * Checks if paragraph is currently active
 */
export function isParagraphActive(editor) {
  if (!editor) return false
  return editor.isActive("paragraph") &&
    !editor.isActive("bulletList") &&
    !editor.isActive("orderedList") &&
    !editor.isActive("taskList") &&
    !editor.isActive("blockquote") &&
    !editor.isActive("callout") &&
    !editor.isActive("codeBlock");
}

/**
 * Converts the current selection or node to paragraph
 */
export function toggleParagraph(editor) {
  if (!editor || !editor.isEditable) return false
  if (!canToggleText(editor)) return false

  return runWithPreservedWindowScroll(() => {
    try {
      // Lists: `paragraph` is already active inside list items, so the generic
      // setNode("paragraph") path below is a no-op. clearNodes() lifts the
      // selected items out of the list, leaving plain paragraphs.
      if (
        editor.isActive("bulletList") ||
        editor.isActive("orderedList") ||
        editor.isActive("taskList")
      ) {
        return editor.chain().focus().clearNodes().run()
      }

      const view = editor.view
      let state = view.state
      let tr = state.tr

      const blocks = getSelectedBlockNodes(editor)

      // In case a selection contains multiple blocks, we only allow
      // toggling to nide if there's exactly one block selected
      // we also dont block the canToggle since it will fall back to the bottom logic
      const isPossibleToTurnInto =
        selectionWithinConvertibleTypes(editor, [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "taskList",
          "blockquote",
          "callout",
          "codeBlock",
        ]) && blocks.length === 1

      // No selection, find the the cursor position
      if (
        (state.selection.empty || state.selection instanceof TextSelection) &&
        isPossibleToTurnInto
      ) {
        const pos = findNodePosition({
          editor,
          node: findConvertibleAncestor(state.selection),
        })?.pos
        if (!isValidPosition(pos)) return false

        tr = tr.setSelection(NodeSelection.create(state.doc, pos))
        view.dispatch(tr)
        state = view.state
      }

      const selection = state.selection
      let chain = editor.chain().focus(undefined, FOCUS_NO_SCROLL)

      // Handle NodeSelection
      if (selection instanceof NodeSelection) {
        const firstChild = selection.node.firstChild?.firstChild
        const lastChild = selection.node.lastChild?.lastChild

        const from = firstChild
          ? selection.from + firstChild.nodeSize
          : selection.from + 1

        const to = lastChild
          ? selection.to - lastChild.nodeSize
          : selection.to - 1

        const resolvedFrom = state.doc.resolve(from)
        const resolvedTo = state.doc.resolve(to)

        chain = chain
          .setTextSelection(TextSelection.between(resolvedFrom, resolvedTo))
          .clearNodes()
      }

      if (!editor.isActive("paragraph")) {
        chain.setNode("paragraph").run()
      }

      editor.chain().focus(undefined, FOCUS_NO_SCROLL).selectTextblockEnd().run()

      return true
    } catch {
      return false
    }
  })
}

/**
 * Determines if the text button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isNodeInSchema("paragraph", editor)) return false

  if (!editor.isActive("code")) {
    return canToggleText(editor);
  }

  return true
}

/**
 * Custom hook that provides text/paragraph functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleTextButton() {
 *   const { isVisible, handleToggle, isActive } = useText()
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleToggle}>Text</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedTextButton() {
 *   const { isVisible, handleToggle, label, isActive } = useText({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: () => console.log('Text converted!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Convert to Text
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useText(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onToggled,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const isMobile = useIsBreakpoint()
  const [isVisible, setIsVisible] = useState(true)
  const canToggle = canToggleText(editor)
  const isActive = isParagraphActive(editor)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    if (!editor) return false

    const success = toggleParagraph(editor)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, onToggled])

  useHotkeys(TEXT_SHORTCUT_KEY, (event) => {
    event.preventDefault()
    handleToggle()
  }, {
    enabled: isVisible && canToggle,
    enableOnContentEditable: !isMobile,
    enableOnFormTags: true,
  })

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle,
    label: "Text",
    shortcutKeys: TEXT_SHORTCUT_KEY,
    Icon: TypeIcon,
  }
}
